import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { API_BASE_URL } from "../../config/api";
import axios from "axios";

// Add this near the top of the file, outside the component
const ADMIN_USER = {
  email: "admin",
  password: "pass",
  permissions: {
    productManagement: true,
    orderManagement: true,
    categoryManagement: true,
    couponsManagement: true,
    inventoryManagement: true,
    marketingManagement: true,
    userManagement: true,
  },
};

function Login() {
  const [formData, setFormData] = useState({
    username: "",
    password: "",
  });
  const [error, setError] = useState("");
  const [showError, setShowError] = useState(false);

  const navigate = useNavigate();

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      // First try built-in admin credentials
      if (formData.username === "admin" && formData.password === "pass") {
        const adminPermissions = {
          productManagement: true,
          orderManagement: true,
          categoryManagement: true,
          couponsManagement: true,
          inventoryManagement: true,
          marketingManagement: true,
          userManagement: true,
        };

        localStorage.setItem(
          "userPermissions",
          JSON.stringify(adminPermissions)
        );
        localStorage.setItem("userRole", "admin");
        localStorage.setItem("lastLoginTime", new Date().toISOString());
        localStorage.setItem("username", "admin");
        navigate("/dashboard");
        return;
      }

      // If not admin, try API login with identifier (username or email)
      const response = await axios.post(`${API_BASE_URL}/api/admin/login`, {
        identifier: formData.username, // Send as identifier instead of email
        password: formData.password,
      });

      if (response.data.message === "Login successful") {
        localStorage.setItem(
          "userPermissions",
          JSON.stringify(response.data.user.permissions)
        );
        localStorage.setItem("userRole", "admin");
        localStorage.setItem("userId", response.data.user._id);
        localStorage.setItem("token", response.data.user.token);
        localStorage.setItem("lastLoginTime", new Date().toISOString());
        localStorage.setItem("username", response.data.user.username);
        navigate("/dashboard");
      } else {
        throw new Error(response.data.message || "Login failed");
      }
    } catch (error) {
      setError(
        error.response?.data?.message || error.message || "Invalid credentials"
      );
      setShowError(true);
      setTimeout(() => {
        setShowError(false);
      }, 3000);
    }
  };

  const ErrorPopup = () => {
    if (!showError) return null;

    return (
      <div
        className="fixed top-4 right-4 bg-white border-l-4 border-red-500 p-4 rounded-md shadow-lg flex items-center space-x-4"
        role="alert"
      >
        <div className="flex-shrink-0">
          <svg
            className="h-6 w-6 text-red-500"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              fillRule="evenodd"
              d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
              clipRule="evenodd"
            />
          </svg>
        </div>
        <div className="flex-1 text-sm font-medium text-gray-800">{error}</div>
        <button
          onClick={() => setShowError(false)}
          className="flex-shrink-0 ml-4 text-gray-400 hover:text-gray-600 transition-colors duration-150"
        >
          <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
            <path
              fillRule="evenodd"
              d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
              clipRule="evenodd"
            />
          </svg>
        </button>
      </div>
    );
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50">
      {/* Add Error Popup */}
      <ErrorPopup />

      {/* Logo moved outside and above the card */}
      <div className="mb-8">
        <div className="w-16 h-16 flex">
          <img src="/logo.png" alt="logo" className="w-16 h-16" />
        </div>
      </div>

      <div className="max-w-md w-full p-6 bg-[#8860D028] rounded-lg shadow-md">
        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label className="block text-gray-700 mb-2">
              Username or email
            </label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z"
                    clipRule="evenodd"
                  />
                </svg>
              </span>
              <input
                type="text"
                name="username"
                value={formData.username}
                onChange={handleChange}
                className="w-full pl-10 pr-3 py-2 border rounded-md"
                placeholder="Username or email"
                required
              />
            </div>
          </div>

          <div className="mb-4">
            <label className="block text-gray-700 mb-2">Password</label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z"
                    clipRule="evenodd"
                  />
                </svg>
              </span>
              <input
                type="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                className="w-full pl-10 pr-3 py-2 border rounded-md"
                placeholder="Password"
                required
              />
            </div>
          </div>

          <button
            type="submit"
            className="w-full bg-purple-600 text-white py-2 rounded-md hover:bg-purple-700"
          >
            Log In
          </button>
        </form>
      </div>
    </div>
  );
}

export default Login;
